import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {DataRouterApplicationBuilder} from '@github-ui/react-core/future/data-router-application'
import type {RouteObject} from '@github-ui/react-router'

import {buildIssuesLayoutRoute} from './issues-layout-route'
import {IssuesLayout} from './IssuesLayout'
import {buildLabelsRoute} from './labels-route'
import {Labels} from './Labels'
import {labelsLegacyRedirectLoader} from './labels-legacy-redirect-loader'
import {buildMilestoneEditRoute} from './milestone-edit-route'
import {MilestoneEdit} from './MilestoneEdit'
import {buildMilestoneNewRoute} from './milestone-new-route'
import {MilestoneNew} from './MilestoneNew'
import {buildMilestoneIndexRoute} from './milestone-index-route'
import {MilestoneIndex} from './MilestoneIndex'

// The loader always redirects, so nothing is ever shown here. But React Router still
// renders the matched route and warns if a leaf route has no Component. this
// fails the tests (vitest-fail-on-console), so we pass an empty component
const LabelsRedirectFallback = () => null

/**
 * Builds the Issues route objects for a given app builder.
 *
 * Route configs are bound to the app builder that creates them (embedded-data
 * hydration + query-key app name), so registering these routes under a different
 * app (e.g. `repo`) requires rebuilding them with that app's builder rather than
 * importing the `issues`-bound singletons directly.
 *
 * Reads the `milestones_react_data_router` client flag, so both call sites (`issues.ts`,
 * `repo.ts`) must invoke it at app registration via the deferred `GetRoutesFunction`,
 * not at module load (unsafe during SSR).
 */
export function createIssuesRoutes(appBuilder: DataRouterApplicationBuilder<string>): RouteObject[] {
  return [
    buildIssuesLayoutRoute(appBuilder).toRoute({
      Component: IssuesLayout,
      children: [
        buildLabelsRoute(appBuilder).toRoute({Component: Labels}),
        // Register milestones/new only when the flag is on, symmetric with the Relay omission in
        // issues-react/index.ts, so the ExtendedLink actions hard-navigate to the legacy page while
        // it's off. See https://github.com/github/issues/issues/22466.
        ...(isFeatureEnabled('milestones_react_data_router')
          ? [buildMilestoneNewRoute(appBuilder).toRoute({Component: MilestoneNew})]
          : []),
        // Same handoff for the migrated milestones/:number/edit route: register it only when
        // milestones_react_data_router is on, mirroring the Relay-route omission in
        // issues-react/index.ts. See https://github.com/github/issues/issues/22450.
        ...(isFeatureEnabled('milestones_react_data_router')
          ? [buildMilestoneEditRoute(appBuilder).toRoute({Component: MilestoneEdit})]
          : []),
        // Milestone index route only when milestones_react_data_router is on. With the flag off,
        // this data-router app doesn't match the path, so links hard-navigate to the Relay page.
        ...(isFeatureEnabled('milestones_react_data_router')
          ? [buildMilestoneIndexRoute(appBuilder).toRoute({Component: MilestoneIndex})]
          : []),
        // defining it as a plain object because its just a legacy-redirect path
        {path: '/:owner/:repo/issues/labels', loader: labelsLegacyRedirectLoader, Component: LabelsRedirectFallback},
      ],
    }),
  ]
}
